/**
 * Image URL optimization utilities for mobile
 */

import { shouldUseLowMemoryMode } from './memoryMonitor';

/**
 * Get optimized thumbnail URL from Supabase Storage
 * Uses Supabase's image transformation API
 */
export const getOptimizedImageUrl = (
  url: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'origin' | 'webp' | 'avif';
  } = {}
): string => {
  try {
    const urlObj = new URL(url);
    
    // Check if it's a Supabase storage URL
    if (!urlObj.hostname.includes('supabase')) {
      return url;
    }
    
    // Adapt quality based on memory pressure
    const lowMemory = shouldUseLowMemoryMode();
    const defaultQuality = lowMemory ? 60 : 75;
    const defaultWidth = lowMemory ? 384 : 512;
    
    const {
      width = defaultWidth,
      height,
      quality = defaultQuality,
      format = 'origin'
    } = options;
    
    // Convert public URL to render endpoint
    const path = urlObj.pathname.replace('/storage/v1/object/public/', '');
    const baseUrl = `${urlObj.protocol}//${urlObj.hostname}`;
    
    // Build transformation params
    const params = new URLSearchParams();
    params.set('width', width.toString());
    if (height) params.set('height', height.toString());
    params.set('quality', quality.toString());
    if (format !== 'origin') params.set('format', format);
    
    return `${baseUrl}/storage/v1/render/image/public/${path}?${params.toString()}`;
  } catch (e) {
    console.error('Error optimizing image URL:', e);
    return url;
  }
};

/**
 * Get thumbnail URL for carousel images
 */
export const getThumbnailUrl = (url: string): string => {
  return getOptimizedImageUrl(url, {
    width: 112,
    height: 112,
    quality: 70
  });
};

/**
 * Get slider-optimized URL (lower res for memory efficiency)
 */
export const getSliderImageUrl = (url: string): string => {
  const lowMemory = shouldUseLowMemoryMode();
  
  return getOptimizedImageUrl(url, {
    width: lowMemory ? 512 : 768,
    quality: lowMemory ? 65 : 80
  });
};

/**
 * Get preview-optimized URL (medium quality for display)
 */
export const getPreviewImageUrl = (url: string): string => {
  const lowMemory = shouldUseLowMemoryMode();
  
  return getOptimizedImageUrl(url, {
    width: lowMemory ? 768 : 1024,
    quality: lowMemory ? 70 : 85
  });
};
